/**
 * The capacity what-ifs, inside section 03 where the balancing happens.
 *
 * These used to sit in section 02: an intern-pool bar above the cluster cards and an
 * Interns / Out pair in every card footer. That made the read-at-a-glance section the busiest thing
 * on the page, and it showed an intern control whether or not the team had an intern.
 *
 * Two rules now:
 *
 *  1. INTERN CONTROLS RENDER ONLY WHEN THE TEAM HOLDS AN INTERN. The pool is derived from the
 *     working-model table rather than typed, so it cannot describe an intern nobody added. T3 has
 *     none, so the whole block is absent until one is added in section 04.
 *  2. ANYONE MARKED "not planned" CAN OFFER THEIR OWN TIME. The Head of TA is on the team with
 *     `countsTowardHiring: false`, so the cockpit never assumes she is recruiting. Lending those
 *     hours is a deliberate act, taken here, and reversible.
 */
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { FIELD_NUM, FIELD_SELECT } from './prioritisationUi';
import { cn } from '../../../../../shared';
import {
  UNASSIGNED,
  type CapacityAssumptions,
  type PrioritisationState,
  type WorkingModel,
} from '../../../types/rolePrioritisation';
import {
  clusterFte,
  fteLabel,
  hasInterns,
  internPoolFte,
  isIntern,
  isPlanned,
  poolAllocated,
  poolAvailableFrom,
  type ClusterStats,
} from '../../../utils/rolePrioritisation';


export function CapacityLevers({
  stats,
  state,
  cap,
  locked,
  onStateChange,
}: {
  stats: ClusterStats[];
  state: PrioritisationState;
  cap: CapacityAssumptions;
  locked: boolean;
  onStateChange: (next: PrioritisationState) => void;
}) {
  const interns = hasInterns(state.team);
  const poolFte = internPoolFte(state.team, cap);
  const available = poolAvailableFrom(state, cap);
  const allocated = poolAllocated(state, stats);
  const free = available - allocated;

  return (
    <div className="border-b border-solid border-pearl">
      {/* Out of office is always relevant: someone is away whether or not an intern exists. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-xs text-iron">
        <span className="text-[13px] font-semibold text-black">Out of office</span>
        {stats.map((c) => {
          const ceiling = Math.max(Math.floor(clusterFte(state.team, c.key, cap)), 1);
          const value = state.out[c.key] ?? 0;
          return (
            <span key={c.key} className="inline-flex items-center gap-1.5">
              {c.key}
              <select
                aria-label={`Recruiters out of office in ${c.key}`}
                disabled={locked}
                value={String(value)}
                onChange={(e) =>
                  onStateChange({
                    ...state,
                    out: { ...state.out, [c.key]: Number(e.target.value) || 0 },
                  })
                }
                className={cn(FIELD_SELECT, 'w-[104px]', value > 0 && 'bg-lightRed text-[#8A0E22]')}
              >
                {Array.from({ length: ceiling + 1 }, (_, n) => (
                  <option key={n} value={String(n)}>
                    {n === 0 ? 'none' : `−${n} FTE`}
                  </option>
                ))}
              </select>
            </span>
          );
        })}
      </div>

      {interns ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-solid border-pearl px-4 py-3 text-xs text-iron">
          <span className="text-[13px] font-semibold text-black">Intern support</span>
          <span className="tabular-nums">
            <b className="font-semibold text-black">{fteLabel(poolFte)}</b> FTE on the team
          </span>
          {stats.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1.5">
              {c.key}
              <input
                type="number"
                min={0}
                max={8}
                step={0.5}
                disabled={locked}
                aria-label={`Interns assigned to ${c.key}`}
                value={String(state.interns[c.key] ?? 0)}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  onStateChange({
                    ...state,
                    interns: { ...state.interns, [c.key]: Number.isNaN(v) || v < 0 ? 0 : v },
                  });
                }}
                className={FIELD_NUM}
              />
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            away
            <select
              aria-label="Interns out of office"
              disabled={locked}
              value={String(state.poolOut ?? 0)}
              onChange={(e) => onStateChange({ ...state, poolOut: Number(e.target.value) || 0 })}
              className={cn(FIELD_SELECT, 'w-[92px]', (state.poolOut ?? 0) > 0 && 'bg-lightRed text-[#8A0E22]')}
            >
              {/* The steps are the interns' OWN FTE, cumulated — a working student is 0.4 FTE, so
                  whole numbers could not say "one of the two is away" and −1 silently wiped a
                  0.4 pool. `internAwaySteps` is exported for the test that pins exactly that. */}
              {internAwaySteps(state.team, cap).map((fte) => (
                <option key={fte} value={String(fte)}>
                  {fte === 0 ? 'none' : `−${fteLabel(fte)} FTE`}
                </option>
              ))}
            </select>
          </span>
          {free < -0.005 ? (
            <Badge variant="red">over-allocated by {fteLabel(-free)} FTE</Badge>
          ) : free > 0.005 ? (
            <span className="text-steel">{fteLabel(free)} FTE unassigned</span>
          ) : (
            <span className="text-steel">fully allocated</span>
          )}
        </div>
      ) : null}

      {/*
        Removed the "Support the team yourself" callout (Theresa, Sep 16 2026): it named her by
        name and dedicated a whole strip to her availability, which she flagged as unnecessary
        prominence for the Head of TA. The mechanism it advertised — anyone marked "not planned"
        can still be selected as a source in the support form below — is unchanged; that dropdown
        lists unplanned people alongside planned ones.
      */}
    </div>
  );
}

/**
 * The intern-absence options: 0, then each intern's FTE cumulated, ending at the whole pool.
 *
 * Two 0.4-FTE working students give 0 / 0.4 / 0.8, so "one of them is away" is expressible. A plain
 * integer list could not say it, and on a single 0.4 intern its only non-zero option (−1) exceeded
 * the pool — clamped back to zero availability, which reads as the control not working.
 */
export function internAwaySteps(team: WorkingModel[], cap: CapacityAssumptions): number[] {
  const each = team
    .filter((p) => isIntern(p) && p.hours > 0)
    .map((p) => p.hours / cap.fullWeekHours)
    .sort((a, b) => a - b);
  const steps = [0];
  each.forEach((fte) => steps.push(round2(steps[steps.length - 1] + fte)));
  return steps;
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/** Whether anyone on the team could be offered as a source of support. */
export function hasSupporters(state: PrioritisationState): boolean {
  return state.team.some((p) => !isPlanned(p) && p.hours > 0 && p.cluster !== UNASSIGNED);
}
